import { Library } from "@observablehq/runtime";
import htm from "htm";
import React from "react";
import ReactDOM from "react-dom/client";

import { type Store } from "@moment/api-collab/differential";
import { type DatabaseTables } from "@moment/api-collab/differential/types";
import { type PageStorageBridge } from "@moment/api-collab/page-local-storage/client";
import { createCellID } from "@moment/prosemirror-utils";
import { type MutationBuilder, type QueryBuilder } from "@moment/state";
import { CellServicesContextProvider, ThemeProvider, useCellServices } from "@moment/ui";

import { VariableObserver } from "../../components/canvas/easel/runtime/VariableObserver";
import { type CanvasRuntimeManager } from "../../components/canvas/easel/runtime/index";
import {
	type VariableObserverType,
	makeUseCellValue,
} from "../../components/canvas/easel/runtime/makeUseCellValue";
import { snippetFromExpression } from "../../components/canvas/easel/runtime/snippetFromExpression";
import { type Env } from "../../utils/common";
import { define } from "../canvas-std-lib/react";
import { createAwsRuntime } from "./awsRuntime";
import { createEnvVarFetchInterceptor } from "./envVarFetchInterceptor";
import { getBlockedWindowProperties, getSafeWindow } from "./safeWindow";

// Re-export QueryBuilder and MutationBuilder for external consumers
export type { MutationBuilder, QueryBuilder };

export type MomentStdLibOptions = {
	runtime: CanvasRuntimeManager;
	env: Env;
	pageStorage: PageStorageBridge;
	documentInteractionMode: "static" | "interactive";
	currentUser: () => AsyncIterable<{ id: string; name: string; profilePicture: string } | null>;
	baseStream: () => AsyncIterable<Store<DatabaseTables> | null>;
	/**
	 * Query builder function that returns a QueryHandle with fetch() and stream() methods.
	 * Used for calling request pages from code cells.
	 */
	query?: QueryBuilder;
	/**
	 * Mutation builder function that returns a MutationHandle with mutateAsync() method.
	 * Used for executing mutations (INSERT/UPDATE/DELETE) from code cells.
	 */
	mutation?: MutationBuilder;
};

export const momentStdLib = (options: MomentStdLibOptions) => {
	const stdLib = define(React, ReactDOM, htm);

	// Install env var fetch interceptor before AWS runtime so the chain is:
	// AWS check → env var substitution + Tauri fetch → browser fetch
	// Only do this for the desktop app for now until we have atlas in the cloud
	createEnvVarFetchInterceptor();

	// This function defines builtin values for observable runtime to use.
	//
	// Unlike variables, builtins cannot depend on the value of other variables or builtins; they
	// are defined with no inputs.
	//
	// Important: If a builtin is defined as a function, it will be invoked lazily to determine the
	// value of the builtin. If you wish for the value of a builtin to be a function, the builtin
	// must be defined either as a promise that resolves to a function or as a function that returns
	// a function. Builtins may also be defined as generators for dynamic values.
	//
	// reference: https://github.com/observablehq/runtime#runtimes

	return Object.assign(new Library(), {
		currentUser: options.currentUser,
		base: options.baseStream,

		// This is the pageStorage for this specific page
		pageStorage: async function* pageStorage() {
			//
			// [MOM-7169] DO NOT CHANGE `pageStorage` UNLESS YOU KNOW WHAT YOU ARE DOING.
			//
			// The runtime has a bug that causes `Promise.resolve(options.pageStorage.data)` to
			// sometimes terminate at unexpected times, which will look to users like a halt to
			// updates from page storage.
			//
			// This was INCREDIBLY PAINFUL TO DEBUG, so if you change this run it by Alex.
			//

			yield* options.pageStorage.data;
		},
		pageStorageCommit: Promise.resolve(options.pageStorage.commit),

		CellServicesContextProvider: Promise.resolve(CellServicesContextProvider),
		useCellServices: Promise.resolve(useCellServices),
		documentInteractionMode: options.documentInteractionMode,

		DesignSystemThemeProvider: { ThemeProvider },

		ReactErrorBoundary: import("react-error-boundary"),
		// moment libaries
		// react
		React: Object.assign({ ...React }, stdLib),
		std: Promise.resolve(stdLib),
		render: Promise.resolve(stdLib.render),
		jsx: Promise.resolve(stdLib.jsx),
		memo: Promise.resolve(stdLib.memo),
		forwardRef: Promise.resolve(stdLib.forwardRef),
		component: Promise.resolve(stdLib.component),
		useCellValue: Promise.resolve(
			makeUseCellValue(
				React,
				createCellID,
				options.runtime,
				snippetFromExpression,
				VariableObserver as unknown as VariableObserverType
			)
		),
		dateFns: import("date-fns"),
		designSystem: import(/* webpackChunkName: 'designSystem' */ "./designSystem").then(
			({ designSystem }) => designSystem
		),

		// HTTP request query builder for calling request pages from code
		query: options.query ? Promise.resolve(options.query) : undefined,

		// Mutation builder for executing mutations from code cells
		mutation: options.mutation ? Promise.resolve(options.mutation) : undefined,

		// env namespace — internal runtime values (not user environment variables)
		env: Promise.resolve({ _aws: createAwsRuntime() }),

		// Overriden Global Variables
		window: getSafeWindow(),
		...getBlockedWindowProperties(),
	});
};
